import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import Swal from "sweetalert2";

import {
  DateInput,
  SelectInput,
  TextInput,
} from "../../components/common/ui/Inputs";

import Button from "../../components/common/ui/Button";
import Table from "../../components/common/table/Table";
import CustomLoader from "../../components/common/ui/Loader";

import { history } from "../../_helpers";

import {
  inmateAction,
  eCardAction,
  publicUserActions,
  masterActions,
} from "../../_store";

import {
  fDate,
  MinDate,
  Today,
} from "../../utils/formatTime";

import ReadIcon from "../../assets/svg/read-tick.svg";
import UnReadIcon from "../../assets/svg/unread-star.svg";
import ExcelExport from "../../components/common/ui/Export/ExcelExport";
import generateXlsx from "../../components/common/export/generateXlsx";
import { showToastNotification } from "../../components/common/ui/NotificationModal/ToastNotication";

function InmateEcard() {
  const dispatch = useDispatch();
  const location = useLocation();

  const { inmate: routeInmate } = location.state || {};
  const paginationTriggered = useRef(false);
  const sortingRef = useRef(false);

  const [showSearchFields, setShowSearchFields] =
    useState(false);

  // Redux State
  const {
    inmateInfoTabData,
    inmate,
    inmateCurrentTab,
  } = useSelector((state) => state.inmate);

  const {
    sentEcardSearchData,
    getSentEcardListResponse,
    loading,
  } = useSelector((state) => state.ecard);


  const { publicUserLastName, publicUserFirstName } =
    useSelector((state) => state.master);

  const EcardReadStatus = useSelector(
    (state) => state.dropdownOptions.EcardReadStatus
  );

  const commonFacilityCode = useSelector(
    (state) => state.auth.commonFacilityCode
  );


  // Common Search Payload
  const getBaseSearchPayload = useCallback(() => {
    return {
      ...sentEcardSearchData,
      FacilityID: commonFacilityCode,
      inmateResidentID: routeInmate?.InmateResidentId || inmate?.InmateResidentId || 0,
      InmateFirstName: routeInmate?.FirstName || inmate?.FirstName || "",
      InmateLastName: routeInmate?.LastName || inmate?.LastName || "",
    };
  }, [
    sentEcardSearchData,
    commonFacilityCode,
    routeInmate,
    inmate,
  ]);

  // Load Ecard List
  const loadEcardList = useCallback(
    (payload) => {
      dispatch(
        eCardAction.updateSentEcardSearch(payload)
      );

      dispatch(
        eCardAction.getSentEcardList(payload)
      );
    },
    [dispatch]
  );

  const handlegetPublicUsername = () => {
    const facilityID = routeInmate?.facilityID || inmate?.facilityID || commonFacilityCode;
    const inmateID = routeInmate?.InmateID || inmate?.InmateID;
    if (inmateID && facilityID) {
      dispatch(masterActions.GetPublicLastName({ facilityID, firstName: "", inmatesId: inmateID, }));
      if (sentEcardSearchData?.PublicLastNameID === 0 || sentEcardSearchData?.PublicLastNameID === undefined) {
        dispatch(masterActions.GetPublicFirstName({ facilityID, lastName: "", inmatesId: inmateID, }));
      }
    }
  }

  // Initial Load
  useEffect(() => {
    if (inmateCurrentTab !== "inmate-ecard-tab") return;
    const search = {
      ...sentEcardSearchData,
      FacilityID: commonFacilityCode,
      inmateResidentID: routeInmate?.InmateResidentId || inmate?.InmateResidentId || 0,
      InmateFirstName: routeInmate?.FirstName || inmate?.FirstName || "",
      InmateLastName: routeInmate?.LastName || inmate?.LastName || "",
      // PageNo: 1,
      //RowCountPerPage: 10,
    };
    dispatch(eCardAction.updateSentEcardSearch(search));
    dispatch(eCardAction.getSentEcardList(search));

    handlegetPublicUsername();
  }, [
    inmateCurrentTab,
    commonFacilityCode,
    routeInmate?.InmateResidentId,
    routeInmate?.FirstName,
    routeInmate?.LastName,
    inmate?.InmateResidentId,
    inmate?.FirstName,
    inmate?.LastName,
  ]);

  // Format Date
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "-";

    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
      .format(new Date(dateString))
      .replace(",", "");
  }, []);

  // Grapheme Split
  const findGraphemeClusters = useCallback(
    (input = "", truncate = false) => {
      const segmenter = new Intl.Segmenter("en", {
        granularity: "grapheme",
      });

      const segments = Array.from(
        segmenter.segment(input)
      ).map((item) => item.segment);

      if (truncate) {
        return `${segments
          .slice(0, 15)
          .join("")}...`;
      }

      return segments;
    },
    []
  );

  // Navigation
  const navigateToEcardInfo = useCallback(
    (id) => {
      Swal.close();

      dispatch(eCardAction.resetForm());

      history.navigate(
        "/Admin/E-Cards/Sent/Details",
        {
          state: {
            ecardID: id,
            source: "InmateEcard",
            Inmate: routeInmate,
          },
        }
      );
    },
    [dispatch]
  );

  const handleNavigatePublicInfo = useCallback(
    (rowData) => {
      Swal.close();

      dispatch(
        publicUserActions.setPublicUserTab(
          "publicuser-info-tab"
        )
      );

      history.navigate(
        "/Admin/PublicUserList/Details",
        {
          state: {
            row: rowData,
            source: "InmateEcard",
            Inmate: routeInmate || inmate
          },
        }
      );
    },
    [dispatch]
  );

  const handleNavigateInmateInfo = useCallback(
    (rowData) => {
      Swal.close();

      dispatch(
        inmateAction.setInmateCurrentTab(
          "inmate-info-tab"
        )
      );

      history.navigate(
        "/Admin/InmateList/Details",
        {
          state: {
            inmate: rowData,
            source: "InmateEcard",
            showTransactionTab: false,
          },
        }
      );
    },
    [dispatch]
  );

  // Pagination
  const handleRowPerPage = useCallback(
    (rowCount) => {
      const search = {
        ...getBaseSearchPayload(),
        PageNo: 1,
        RowCountPerPage: rowCount,
      };

      loadEcardList(search);
    },
    [getBaseSearchPayload, loadEcardList]
  );

  const handlePageChange = useCallback(
    (page) => {
      if (sortingRef.current) {
        sortingRef.current = false;
        return;
      }

      if (paginationTriggered.current) {
        paginationTriggered.current = false;
        return;
      }

      const search = {
        ...getBaseSearchPayload(),
        PageNo: page,
      };

      loadEcardList(search);
    },
    [getBaseSearchPayload, loadEcardList]
  );

  // Sorting
  const handleSort = useCallback(
    (column) => {
      if (!column?.key) return;

      sortingRef.current = true;

      const currentSort =
        sentEcardSearchData.SortBy || "";

      const sortDirection =
        currentSort.includes(column.key) &&
          currentSort.endsWith("ASC")
          ? "DESC"
          : "ASC";

      const search = {
        ...getBaseSearchPayload(),
        SortBy: `${column.key} ${sortDirection}`,
      };

      loadEcardList(search);
    },
    [
      sentEcardSearchData,
      getBaseSearchPayload,
      loadEcardList,
    ]
  );

  // Input Change
  const handleChange = useCallback(
    (e, inputName) => {
      paginationTriggered.current = true;

      const search = {
        ...getBaseSearchPayload(),
      };

      if (inputName) {
        search[inputName] = fDate(e) || "";
        search.PageNo = 1;

        dispatch(
          eCardAction.updateSentEcardSearch(search)
        );

        return;
      }

      const { name, value } = e.target;

      search[name] = value;

      dispatch(
        eCardAction.updateSentEcardSearch(search)
      );
    },
    [dispatch, getBaseSearchPayload]
  );

  // Date Change
  const handleDateChange = useCallback(
    (value, name) => {
      const search = {
        ...getBaseSearchPayload(),
        [name]: fDate(value) || "",
      };

      dispatch(
        eCardAction.updateSentEcardSearch(search)
      );
    },
    [dispatch, getBaseSearchPayload]
  );

  // Date Key
  const handleDateEnterKey = (event, name) => {
    const updateSearch = {
      ...sentEcardSearchData,
    };
    if (event.key === "Enter" && updateSearch[name] === "") {
      const currentDate = new Date();

      // Set today's date using the same logic as your handleDateChange
      handleChange(currentDate, name);
    }

    const regex = /^[0-9/]$/; // Allow numbers and "/"

    if (
      !regex.test(event.key) &&
      event.key !== "Backspace" &&
      event.key !== "Delete" &&
      !(event.ctrlKey && event.key === "v")
    ) {
      event.preventDefault();
    }

    // Auto-insert '/' when typing
    const input = event.target.value;

    if (event.key !== "Backspace" && event.key !== "Delete") {
      if (input?.length === 2 || input?.length === 5) {
        event.target.value = input + "/";
      }
    }
  };
  // Date Blur
  const handleDateBlur = useCallback(
    (e, fieldName) => {
      const value = e.target.value;

      const startDate =
        fieldName === "EcardSendStartDate"
          ? value
          : sentEcardSearchData.EcardSendStartDate;

      const endDate =
        fieldName === "EcardSendEndDate"
          ? value
          : sentEcardSearchData.EcardSendEndDate;

      const minDate = MinDate();
      const today = Today();

      const valueDate = new Date(value);

      const isValidRange =
        !startDate ||
        !endDate ||
        new Date(startDate) <=
        new Date(endDate);

      const isValidDate =
        valueDate >= minDate &&
        valueDate <= today;

      const search = {
        ...sentEcardSearchData,
        [fieldName]:
          isValidRange && isValidDate
            ? value
            : "",
      };

      dispatch(
        eCardAction.updateSentEcardSearch(search)
      );
    },
    [dispatch, sentEcardSearchData]
  );

  // Search Submit
  const handleSearchSubmit = useCallback(
    (e) => {
      e.preventDefault();

      const search = {
        ...getBaseSearchPayload(),
        PageNo: 1,
        RowCountPerPage: 10,
      };

      loadEcardList(search);
    },
    [getBaseSearchPayload, loadEcardList]
  );

  // Reset
  const handleSearchReset = useCallback(() => {
    paginationTriggered.current = false;

    const search = {
      ...getBaseSearchPayload(),

      PageNo: 1,
      RowCountPerPage: 10,

      PublicFirstName: "",
      PublicLastName: "",
      PublicFirstNameID: 0,
      PublicLastNameID: 0,

      EcardSubject: "",
      EcardSendStartDate: "",
      EcardSendEndDate: "",
      EcardReadStatus: 0,
      SortBy: "",
    };

    loadEcardList(search);
    handlegetPublicUsername();
  }, [
    getBaseSearchPayload,
    loadEcardList,
  ]);


  const handleSelectChange = useCallback(
    (selectedOption, name) => {
      const search = {
        ...sentEcardSearchData,
        FacilityID: commonFacilityCode,
        PageNo: 1,
        RowCountPerPage: 10,
      };

      switch (name) {
        case "PublicLastName":
          search.PublicLastName = selectedOption?.value > 0 ? selectedOption?.label : "";
          search.PublicLastNameID = selectedOption?.value || 0;
          search.PublicFirstName = "";
          search.PublicFirstNameID = 0;

          dispatch(masterActions.GetPublicFirstName({
            facilityID: search.FacilityID,
            lastName: search.PublicLastName,
            inmatesId: routeInmate.InmateID,
          })
          );

          break;

        case "PublicFirstName":
          search.PublicFirstName = selectedOption?.value > 0 ? selectedOption?.label : "";
          search.PublicFirstNameID = selectedOption?.value || 0;
          break;

        default:
          search[name] =
            selectedOption?.value !== undefined
              ? selectedOption.value
              : 0;

          break;
      }

      loadEcardList(search);
    },
    [
      getBaseSearchPayload, loadEcardList
    ]
  );

  // Table Columns
  const columns = useMemo(
    () => [
      {
        width: "210px",
        name: "Subject",
        key: "EcardSubject",
        selector: (row) => row.EcardSubject,
        sortable: true,

        cell: (row) => (
          <div className="d-flex flex-wrap gap-2 align-items-start">
            <div
              title={row.EcardSubject}
              className="Common_Link_Style"
              onClick={() =>
                navigateToEcardInfo(row.CardId)
              }
            >
              {findGraphemeClusters(
                row.EcardSubject,
                false
              )?.length > 15
                ? findGraphemeClusters(
                  row.EcardSubject,
                  true
                )
                : row.EcardSubject}
            </div>

            {row?.UserPhotoReadStatus === 0 && (
              <div className="newbutton-container">
                New
                <div className="border-animation"></div>
              </div>
            )}
          </div>
        ),
      },

      {
        width: "210px",
        name: "From",
        key: "EcardFrom",
        selector: (row) => row.EcardFrom,
        sortable: true,

        cell: (row) => (
          <div className="d-flex gap-2 align-items-center">
            <div
              className={
                row?.Sender === "Public User"
                  ? "user-batch button-success"
                  : "user-batch button-primary"
              }
            />

            <div
              className="Common_Link_Style"
              onClick={() =>
                row?.Sender === "Public User"
                  ? handleNavigatePublicInfo(row)
                  : handleNavigateInmateInfo(row)
              }
            >
              {row.EcardFrom}
            </div>
          </div>
        ),
      },

      {
        width: "210px",
        name: "To",
        key: "EcardTo",
        selector: (row) => row.EcardTo,
        sortable: true,

        cell: (row) => (
          <div className="d-flex gap-2 align-items-center">
            <div
              className={
                row?.Receiver === "Public User"
                  ? "user-batch button-success"
                  : "user-batch button-primary"
              }
            />

            <div
              className="Common_Link_Style"
              onClick={() =>
                row?.Receiver === "Inmate"
                  ? handleNavigateInmateInfo(row)
                  : handleNavigatePublicInfo(row)
              }
            >
              {row.EcardTo}
            </div>
          </div>
        ),
      },

      {
        width: "120px",
        name: "Inmate ID",
        key: "InmateResidentId",
        selector: (row) =>
          row.InmateResidentId,

        sortable: true,

        cell: (row) => (
          <div
            className="Common_Link_Style"
            onClick={() =>
              handleNavigateInmateInfo(row)
            }
          >
            {row.InmateResidentId}
          </div>
        ),
      },

      {
        width: "120px",
        name: "E-Card Cost($)",
        key: "EcardCost",
        selector: (row) =>
          Number(row.EcardCost || 0).toFixed(2),

        sortable: true,
      },

      {
        width: "180px",
        name: "Sent On",
        key: "Sentdate",
        selector: (row) => row.Sentdate,
        sortable: true,

        cell: (row) => (
          <div className="font-size-13 fw-500">
            {formatDate(row?.Sentdate)}
          </div>
        ),
      },

      {
        width: "120px",
        name: "Status",
        key: "ReadStaus",
        selector: (row) => row.ReadStaus,
        sortable: true,

        cell: (row) =>
          row.ReadStaus === "Read" ? (
            <span
              title="This e-card has been opened and read by the recipient."
              className="ecard-read"
            >
              <img
                src={ReadIcon}
                alt="read-icon"
              />

              <span> Read</span>
            </span>
          ) : (
            <span
              title="This e-card has not yet been opened by the recipient."
              className="ecard-unread"
            >
              <img
                src={UnReadIcon}
                alt="unread-icon"
              />

              <span> Unread</span>
            </span>
          ),
      },
    ],
    [
      navigateToEcardInfo,
      handleNavigatePublicInfo,
      handleNavigateInmateInfo,
      findGraphemeClusters,
      formatDate,
    ]
  );

  // Search UI
  const renderSearch = () => (
    <form onSubmit={handleSearchSubmit}>
      <div className="row">
        <div className="p-2 m-1 d-flex flex-wrap align-items-center">
          {/* Frequency */}
          <div className="col-sm-12 col-md-4 d-flex flex-wrap align-items-center me-1">
            <label className="fs-5" style={{ minWidth: "150px" }}>
              Inmate Last Name :
            </label>
            <strong className="fs-5 text-end mx-2">
              {routeInmate?.LastName ||
                inmate?.LastName ||
                (inmateInfoTabData && inmateInfoTabData[0]?.LastName)}
            </strong>
          </div>

          {/* Limit */}
          <div className="col-sm-12 col-md-4 d-flex flex-wrap align-items-center">
            <label className="fs-5" style={{ minWidth: "150px" }}>
              Inmate First Name :
            </label>
            <strong className="fs-5 text-end mx-2">
              {routeInmate?.FirstName ||
                inmate?.FirstName ||
                (inmateInfoTabData && inmateInfoTabData[0]?.FirstName)}
            </strong>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-6 col-md-4">
          <div className="form-floating mb-3">
            <SelectInput
              options={publicUserLastName}
              value={
                sentEcardSearchData?.PublicLastNameID
              }
              handleChange={(selectedOption) => handleSelectChange(selectedOption, "PublicLastName")}
              isMulti={false}
              closeMenuOnSelect
              isClearable={sentEcardSearchData?.PublicLastNameID !== 0}
              placeholder="Select Last Name"
              label="Public User Last Name"
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-4">
          <div className="form-floating mb-3">
            <SelectInput
              options={publicUserFirstName}
              value={
                sentEcardSearchData?.PublicFirstNameID
              }
              handleChange={(selectedOption) => handleSelectChange(selectedOption, "PublicFirstName")
              }
              isMulti={false}
              closeMenuOnSelect
              isClearable={sentEcardSearchData?.PublicFirstNameID !== 0}
              placeholder="Select First Name"
              label="Public User First Name"
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-4">
          <div className="form-floating mb-3">
            <TextInput
              name="EcardSubject"
              label="Subject"
              value={sentEcardSearchData?.EcardSubject}
              handleChange={handleChange}
              placeholder="Enter Subject"
              maxLength={50}
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-4">
          <div className="form-floating mb-3">
            <DateInput
              value={sentEcardSearchData?.EcardSendStartDate}
              handleChange={(e) => handleDateChange(e, "EcardSendStartDate")}
              name="EcardSendStartDate"
              label="From Date"
              maxDate={sentEcardSearchData?.EcardSendEndDate || new Date()}
              minDate={new Date(2017, 0, 1)}
              isClearable={!!sentEcardSearchData?.EcardSendStartDate}
              handleBlur={(e) => handleDateBlur(e, "EcardSendStartDate")}
              handleKeyDown={(e) =>
                handleDateEnterKey(
                  e,
                  "EcardSendStartDate"
                )
              }
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-4">
          <div className="form-floating mb-3">
            <DateInput
              value={
                sentEcardSearchData?.EcardSendEndDate
              }
              handleChange={(e) =>
                handleDateChange(
                  e,
                  "EcardSendEndDate"
                )
              }
              name="EcardSendEndDate"
              label="To Date"
              minDate={
                sentEcardSearchData?.EcardSendStartDate ||
                new Date(2017, 0, 1)
              }
              maxDate={new Date()}
              isClearable={
                !!sentEcardSearchData?.EcardSendEndDate
              }
              handleBlur={(e) =>
                handleDateBlur(
                  e,
                  "EcardSendEndDate"
                )
              }
              handleKeyDown={(e) =>
                handleDateEnterKey(
                  e,
                  "EcardSendEndDate"
                )
              }
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-4">
          <div className="form-floating mb-3">
            <SelectInput
              options={EcardReadStatus}
              value={
                sentEcardSearchData?.EcardReadStatus
              }
              handleChange={(selectedOption) =>
                handleSelectChange(
                  selectedOption,
                  "EcardReadStatus"
                )
              }
              isMulti={false}
              closeMenuOnSelect
              isClearable={
                sentEcardSearchData?.EcardReadStatus !==
                0
              }
              placeholder="Select Status"
              label="Status"
            />
          </div>
        </div>
      </div>

      <div className="mt-3 d-flex flex-wrap justify-content-center gap-3">
        <Button
          label="SEARCH"
          type="submit"
          status="primary"
          icon="search"
        />

        <Button
          label="RESET"
          type="button"
          variant="danger"
          status="secondary"
          icon="fa-solid fa-rotate-right"
          onClick={handleSearchReset}
        />
      </div>
    </form>
  );

  // Loader
  if (loading) {
    return <CustomLoader />;
  }

  const handleExcel = () => {
    let updatedParam = {
      ...getBaseSearchPayload(),
      PageNo: 1,
      exportExcel: true,
      RowCountPerPage: getSentEcardListResponse?.TotalECardCount,
    };
    dispatch(eCardAction.getSentEcardList(updatedParam)).then((x) => {
      let title = "E-Card_List";
      let generateExcelColumns = [
        "Subject",
        "From",
        "To",
        "E-Card Cost($)",
        "Sent On",
        "Status",
      ];

      // Map the account manager list to match the Excel format
      let PUPhoto_List =
        x?.payload?.DataList?.map((obj) => ({
          Subject: obj.EcardSubject,
          From: obj.EcardFrom,
          To: obj.EcardTo,
          Cost: obj.EcardCost.toFixed(2),
          Sent: formatDate(obj.Sentdate),
          Status: obj.ReadStaus,
        })) || [];

      try {
        // Generate the Excel file with the transformed data
        generateXlsx(PUPhoto_List, generateExcelColumns, title);
      } catch (error) {
        // Handle any errors that occur during Excel generation
        showToastNotification("error", "error");
      }
    })
  };

  return (
    <div>
      {/* Header */}
      <div className="p-2">
        <div className="d-flex flex-wrap justify-content-between align-items-center">
          <h4 className="inner-subheader-bg text-color">
            <i className="fa fa-camera me-2"></i>
            E-Cards
          </h4>

          <Button
            label="Back"
            type="button"
            onClick={() =>
              history.navigate("/Admin/InmateList")
            }
            variant="danger"
            icon="fa-solid fa-angle-left"
            status="primary"
          />
        </div>
      </div>

      {/* Search Card */}
      <div className="card shadow-sm b-r-all p-4 border border-dark-grey w-100">
        {/* Mobile Header */}
        <div className="d-block d-md-none">
          <div
            className="d-flex align-items-center justify-content-between"
            style={{
              background: "transparent",
              cursor: "pointer",
            }}
            onClick={() =>
              setShowSearchFields((prev) => !prev)
            }
          >
            <h2 className="form_title_uppercase text-color">
              SEARCH
            </h2>

            <i
              className={`fa fa-angle-down ${showSearchFields ? "rotate" : ""
                }`}
              style={{ fontSize: "20px" }}
            />
          </div>
        </div>

        {/* Mobile Search */}
        <div className="d-block d-md-none mt-2">
          {showSearchFields && renderSearch()}
        </div>

        {/* Desktop Search */}
        <div className="d-none d-md-block">
          <div className="mb-3">
            <h2 className="form_title_uppercase text-color">
              SEARCH
            </h2>
          </div>

          {renderSearch()}
        </div>
      </div>

      {/* Table */}
      <div className="card shadow-sm b-r-all p-4 border border-dark-grey mt-3 w-100 mb-4">
        <div className="d-flex flex-wrap  justify-content-end mb-3">
          {getSentEcardListResponse?.DataList?.length > 0 && (
            <ExcelExport variant="soft-gray" handleExcelExport={handleExcel} />
          )}
        </div>

        <Table
          columns={columns}
          data={
            getSentEcardListResponse?.DataList ||
            []
          }
          pagination
          onChangeRowsPerPage={handleRowPerPage}
          onChangePage={handlePageChange}
          totalRowCount={getSentEcardListResponse?.TotalECardCount || 0}
          currentPage={sentEcardSearchData?.PageNo}
          paginationPerPage={sentEcardSearchData?.RowCountPerPage}
          onSort={handleSort}
          serverSideSort
          sortColumn={sentEcardSearchData?.SortBy?.split(" ")[0]}
          sortDirection={sentEcardSearchData?.SortBy?.split(" ")[1]}
        />
      </div>
    </div>
  );
}

export default React.memo(InmateEcard);